/**
 * Entities for the "main" module used by the Logo Generator.
 * These are shared across presentation, domain hooks, and API layers.
 */

export enum PaletteId {
  NEON = "neon",
  MONO = "mono",
  WARM = "warm",
  COOL = "cool",
  PASTEL = "pastel",
}

/**
 * Represents a selectable color palette option.
 * swatches should include 3-6 hex colors used as hints to the generator.
 */
export interface ColorPalette {
  id: PaletteId;
  name: string;
  swatches: string[]; // hex values, e.g., ["#ff2bd3", "#0ffbdf", ...]
}

/**
 * Represents a selectable font option for text within the generated logo.
 * cssFamily should be a valid CSS font-family string (e.g., "'Press Start 2P', cursive").
 */
export interface FontOption {
  id: string; // unique id (e.g., "press-start-2p")
  name: string; // human-friendly label (e.g., "Press Start 2P")
  cssFamily: string; // CSS font-family value
}

/**
 * The complete set of inputs that inform a logo generation request.
 * - styleImageUrl: external URL to a style reference image (optional)
 * - styleImageFileName: if a local file is uploaded, the domain layer may upload to a blob and keep the remote filename or a client blob URL.
 * - styleImageBase64: raw base64 string (no data: prefix) for the uploaded file (takes precedence over styleImageUrl if provided)
 * - styleImageMimeType: MIME type of the uploaded file (must be one of 'image/png','image/jpeg','image/gif','image/webp')
 */
export interface LogoGenerationInput {
  prompt: string;
  styleImageUrl: string | null;
  styleImageFileName: string | null;
  styleImageBase64: string | null;
  styleImageMimeType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp' | null;
  paletteId: PaletteId;
  size: number; // in px, e.g., 128..1024
  fontId: string;
}

/**
 * Represents a generated SVG logo result.
 */
export interface GeneratedLogo {
  id: string;
  svgMarkup: string; // validated, sanitized raw SVG markup to render
  size: number;
  paletteId: PaletteId;
  fontId: string;
  createdAt: Date;
}
